/**
 * These are the cards... They're a front and back div (back-side flipped in 3D space, with some backface-visibility stuff),
 * sitting inside another div to contain it all. Rather than flipping this wrapper div (desired), to support IE we must
 * flip the front and back faces individually in a way that creates the illusion that the wrapper is being flipped.
 */

import PropTypes from 'prop-types'

import React from 'react'
import { css } from 'lib/paypalphrodite'
import ContactThumbnail from './new-contact-thumbnail'
import classNames from 'classnames'
import styles from './theme-card.styles'
import Media from 'react-media'
import content from 'pp-react-l10n'
let i18n = content('transfer/themes')

class ThemeCard extends React.Component {
  // TODO: when we move to the new i18n technique this can be converted into a stateless component
  static propTypes = {
    editableMessage: PropTypes.bool.isRequired, // sender experience is editable, recipient should just display
    displayName: PropTypes.string.isRequired,
    formattedAmount: PropTypes.string.isRequired,
    cardWrapperStyle: PropTypes.object, // this is the react-ready style object built from the parent <Motion />
    cardFrontStyle: PropTypes.object, // this is the react-ready style object built from the parent <Motion />
    cardBackStyle: PropTypes.object, // this is the react-ready style object built from the parent <Motion />
    note: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    footerThemeImageUrl: PropTypes.string,
    isNoteFieldTabEnabled: PropTypes.bool,
    handleChangeNote: PropTypes.func,
    handleCardFrontHover: PropTypes.func,
    handleCardFrontBlur: PropTypes.func,
    handleCardFrontClick: PropTypes.func,
    themeId: PropTypes.string,
    isNewDesign: PropTypes.bool,
  }

  static defaultProps = {
    cardWrapperStyle: {},
    note: '',
    thumbnailUrl: '',
    handleChangeNote: () => {},
    isNoteFieldTabEnabled: true,
    isNewDesign: false,
  }

  /**
   * Helper to render an editable textarea, or a non-editable <p>
   */
  renderMessageBox = () => {
    const {
      editableMessage,
      handleChangeNote,
      note,
      thumbnailUrl,
      isNewDesign,
    } = this.props

    // for either editable message or static message, if there is not thumbnail, we can make the message space larger to show more text
    const shouldUseLargeHeight = !thumbnailUrl
    const textAreaClassNames = {
      [css(styles.textArea)]: true,
      [css(styles.newTextArea)]: isNewDesign,
      [css(styles.messageLargeHeight)]: shouldUseLargeHeight && !isNewDesign,
      [css(styles.newMessageLargeHeight)]: shouldUseLargeHeight && isNewDesign,
      [css(styles.messageSmallHeight)]: !shouldUseLargeHeight,
    }
    const staticMessageClassNames = {
      [css(styles.staticMessage)]: true,
      [css(styles.messageLargeHeight)]: shouldUseLargeHeight,
      [css(styles.messageSmallHeight)]: !shouldUseLargeHeight,
    }

    if (editableMessage) {
      return (
        <textarea
          tabIndex={this.props.isNoteFieldTabEnabled ? '0' : '-1'}
          value={note}
          onChange={({ target: { value } }) => handleChangeNote(value)}
          placeholder={
            isNewDesign
              ? i18n('card.notePlaceholder')
              : i18n('card.placeholder')
          }
          className={classNames(textAreaClassNames)}
        />
      )
    }

    return (
      <p
        data-test-id="theme-card-static-note"
        className={classNames(staticMessageClassNames)}
      >
        {note}
      </p>
    )
  }

  /**
   * Renders a smaller-sized transactionDetails depending on if there is a thumbnailUrl
   */
  renderTransactionDetails = () => {
    const {
      thumbnailUrl,
      displayName,
      formattedAmount,
      isNewDesign,
    } = this.props
    const shouldUseSmallerSize = !!thumbnailUrl

    const transactionDetailsClassNames = {
      [css(styles.transactionDetails)]: true,
      [css(styles.transactionDetailsSmallerSize)]: shouldUseSmallerSize,
      [css(styles.newTransactionDetails)]: isNewDesign,
    }

    return (
      <h3 className={classNames(transactionDetailsClassNames)}>
        {isNewDesign
          ? i18n('card.transactionMessage', { displayName })
          : i18n('card.transactionDetails', { displayName, formattedAmount })}
        {isNewDesign && (
          <span className={css(styles.transactionAmount)}>
            {' '}
            {formattedAmount}
          </span>
        )}
      </h3>
    )
  }

  renderContactThumbnail = () => {
    return (
      <Media query={'(max-width: 450px)'}>
        {match =>
          match ? (
            <ContactThumbnail
              diameter={50}
              className={css(styles.contactClassName)}
              thumbnailUrl={this.props.thumbnailUrl}
            />
          ) : (
            <ContactThumbnail
              diameter={70}
              className={css(styles.contactClassName)}
              thumbnailUrl={this.props.thumbnailUrl}
            />
          )
        }
      </Media>
    )
  }

  render() {
    const {
      cardWrapperStyle,
      cardFrontStyle,
      cardBackStyle,
      handleCardFrontHover,
      handleCardFrontBlur,
      thumbnailUrl,
      handleCardFrontClick,
      footerThemeImageUrl,
      isNewDesign,
    } = this.props

    let frontStyles = styles.cardFront

    return (
      <div style={cardWrapperStyle} className={css(styles.cardWrapper)}>
        <div
          aria-label={i18n('a11y.theme.personalize')}
          role="button"
          tabIndex="0"
          data-theme-id={this.props.themeId}
          onClick={handleCardFrontClick}
          onKeyUp={event => {
            if (event.keyCode === 32) {
              // if it's the space key
              handleCardFrontClick()
            }
          }}
          onMouseEnter={handleCardFrontHover}
          onMouseLeave={handleCardFrontBlur}
          style={cardFrontStyle}
          className={`${css(frontStyles)} unit-test-card-front`}
        />
        {isNewDesign ? (
          <div
            style={cardBackStyle}
            className={css(styles.cardBack, styles.newCardBack)}
          >
            {thumbnailUrl && this.renderContactThumbnail()}
            {this.renderTransactionDetails()}
            {this.renderMessageBox()}
            <div className={css(styles.footerThemeImage)} />
          </div>
        ) : (
          <div style={cardBackStyle} className={css(styles.cardBack)}>
            {thumbnailUrl && this.renderContactThumbnail()}
            {this.renderTransactionDetails()}
            {this.renderMessageBox()}
            <div
              style={{ backgroundImage: `url('${footerThemeImageUrl}')` }}
              className={css(styles.footerThemeImage)}
            />
          </div>
        )}
      </div>
    )
  }
}

export default ThemeCard
